import React, { useEffect, useState } from 'react';
import integrationAdminApi from '../services/integrationAdminApi';

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function formatUsageType(value) {
  return String(value || '-').replace(/_/g, ' ');
}

function formatConsentStatus(value) {
  const status = String(value || 'pending').toLowerCase();
  if (status === 'approved') return 'Approved';
  if (status === 'denied') return 'Denied';
  return 'Pending';
}

export default function IntegrationMerchants() {
  const [status, setStatus] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [usage, setUsage] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function loadMerchants() {
    setLoading(true);
    setError('');
    try {
      const data = await integrationAdminApi.listMerchants(status ? { status } : {});
      setMerchants(data.merchants || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load merchants');
    } finally {
      setLoading(false);
    }
  }

  async function selectMerchant(merchant) {
    setSelected(merchant);
    const [companyData, keyData, usageData] = await Promise.all([
      integrationAdminApi.listMerchantCompanies(merchant.id),
      integrationAdminApi.listMerchantApiKeys(merchant.id),
      integrationAdminApi.getMerchantUsage(merchant.id).catch(() => ({ usage: [] }))
    ]);
    setCompanies(companyData.companies || []);
    setApiKeys(keyData.apiKeys || []);
    setUsage(usageData.usage || []);
  }

  useEffect(() => {
    loadMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function perform(action, merchant) {
    if (action === 'approve') await integrationAdminApi.approveMerchant(merchant.id);
    if (action === 'reject') {
      const reason = window.prompt('Reject reason') || '';
      await integrationAdminApi.rejectMerchant(merchant.id, reason);
    }
    if (action === 'disable') await integrationAdminApi.disableMerchant(merchant.id);
    await loadMerchants();
    if (selected?.id === merchant.id) {
      const data = await integrationAdminApi.getMerchant(merchant.id);
      await selectMerchant(data.merchant);
    }
  }

  async function addCompany(event) {
    event.preventDefault();
    if (!selected || !companyId) return;
    await integrationAdminApi.addMerchantCompany(selected.id, Number(companyId));
    setCompanyId('');
    await selectMerchant(selected);
  }

  async function removeCompany(row) {
    if (!selected) return;
    await integrationAdminApi.removeMerchantCompany(selected.id, row.companyId);
    await selectMerchant(selected);
  }

  async function revokeKey(key) {
    if (!selected) return;
    await integrationAdminApi.revokeApiKey(selected.id, key.id);
    await selectMerchant(selected);
  }

  return (
    <div className="admin-integrations">
      <div className="integration-toolbar">
        <div>
          <h2>Integration merchants</h2>
          <p className="muted">Review partner accounts, whitelist companies, manage API keys, and inspect usage.</p>
        </div>
        <div className="admin-inline-controls admin-usage-controls">
          <div className="control-group">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="rejected">Rejected</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <button type="button" className="btn-secondary small" onClick={loadMerchants} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-two-column">
        <div className="dashboard-card integration-card integration-list-card">
          <div className="integration-card-header">
            <div>
              <h3>Merchants</h3>
              <p className="muted">{merchants.length} account{merchants.length === 1 ? '' : 's'} found</p>
            </div>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table integration-merchants-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {merchants.map((merchant) => (
                  <tr key={merchant.id} className={selected?.id === merchant.id ? 'selected-row' : ''}>
                    <td>
                      <button type="button" className="admin-link-btn" onClick={() => selectMerchant(merchant)}>
                        {merchant.companyName || merchant.name}
                      </button>
                      <span className="integration-row-meta">Created {formatDate(merchant.createdAt)}</span>
                    </td>
                    <td>{merchant.contactEmail}</td>
                    <td>{merchant.contactPhone || '-'}</td>
                    <td><span className="admin-pill">{merchant.status}</span></td>
                    <td>
                      {merchant.status === 'pending' && (
                        <>
                          <button type="button" className="admin-link-btn" onClick={() => perform('approve', merchant)}>Approve</button>
                          <button type="button" className="admin-link-btn" onClick={() => perform('reject', merchant)}>Reject</button>
                        </>
                      )}
                      {merchant.status === 'active' && (
                        <button type="button" className="admin-link-btn" onClick={() => perform('disable', merchant)}>Disable</button>
                      )}
                    </td>
                  </tr>
                ))}
                {!merchants.length && (
                  <tr>
                    <td colSpan="5" className="integration-empty-cell">
                      {loading ? 'Loading merchants...' : 'No merchants match this filter.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-card integration-card integration-detail-card">
          <div className="integration-card-header">
            <div>
              <h3>{selected ? (selected.companyName || selected.name) : 'Select a merchant'}</h3>
              <p className="muted">
                {selected ? `${selected.contactEmail} · ${selected.status}` : 'Choose a merchant from the table to view details.'}
              </p>
            </div>
            {selected && <span className="admin-pill">{selected.status}</span>}
          </div>
          {selected && (
            <>
              <div className="integration-summary-grid">
                <div>
                  <span>Email</span>
                  <strong>{selected.contactEmail}</strong>
                </div>
                <div>
                  <span>Phone</span>
                  <strong>{selected.contactPhone || '-'}</strong>
                </div>
              </div>

              <form className="admin-inline-controls" onSubmit={addCompany}>
                <input placeholder="Company ID to whitelist" value={companyId} onChange={(e) => setCompanyId(e.target.value)} />
                <button type="submit" className="btn-secondary small">Whitelist company</button>
              </form>

              <div className="integration-detail-section">
                <h4>Whitelisted companies</h4>
                <div className="admin-table-wrap integration-compact-wrap">
                  <table className="admin-table integration-compact-table">
                    <tbody>
                      {companies.map((row) => (
                        <tr key={row.companyId}>
                          <td>{row.companyName || row.companyId}</td>
                          <td><span className={`admin-pill consent-${row.consentStatus || 'pending'}`}>{formatConsentStatus(row.consentStatus)}</span></td>
                          <td><span className="admin-pill">{row.isActive ? 'Active' : (row.consentStatus === 'approved' ? 'Revoked' : 'Blocked')}</span></td>
                          <td>
                            {row.consentStatus === 'approved' && row.isActive ? (
                              <button type="button" className="admin-link-btn" onClick={() => removeCompany(row)}>Revoke</button>
                            ) : (
                              <button type="button" className="admin-link-btn" disabled>Revoke</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!companies.length && (
                        <tr>
                          <td colSpan="4" className="integration-empty-cell">No companies whitelisted yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="integration-detail-section">
                <h4>API keys</h4>
                <div className="admin-table-wrap integration-compact-wrap">
                  <table className="admin-table integration-compact-table">
                    <tbody>
                      {apiKeys.map((key) => (
                        <tr key={key.id}>
                          <td><code>{key.keyPrefix}</code></td>
                          <td>{key.label || 'Untitled key'}</td>
                          <td><span className="admin-pill">{key.isActive ? 'Active' : 'Revoked'}</span></td>
                          <td>{key.isActive && <button type="button" className="admin-link-btn" onClick={() => revokeKey(key)}>Revoke</button>}</td>
                        </tr>
                      ))}
                      {!apiKeys.length && (
                        <tr>
                          <td colSpan="4" className="integration-empty-cell">No API keys created.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="integration-detail-section">
                <h4>Usage</h4>
                <div className="admin-table-wrap integration-compact-wrap">
                  <table className="admin-table integration-compact-table">
                    <tbody>
                      {usage.map((row, idx) => (
                        <tr key={`${row.date}-${row.usageType}-${idx}`}>
                          <td>{row.date}</td>
                          <td>{formatUsageType(row.usageType)}</td>
                          <td>{row.count || 0} calls</td>
                          <td>{row.failedCount || 0} failed</td>
                        </tr>
                      ))}
                      {!usage.length && (
                        <tr>
                          <td colSpan="4" className="integration-empty-cell">No usage recorded.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
